import { type ReactNode } from 'react'
import { type PermissionEntityId } from '@terros/common'
import { Text, UnstyledButton } from '@mantine/core'
import { stringifyPermissionScope } from '../util'
import { MaterialSymbolIcon } from '../../terros/icon'
import { type PermGraphNode } from './types'
import { PermGraphWinnerLabel } from './PermGraphWinnerLabel'
import {
  getPermGraphAccessTone,
  getPermGraphBorderRole,
  getPermGraphNodeIcon,
  getPermGraphNodeStateLabel,
  getPermGraphNodeTheme,
} from './permGraphVisualState'
import { getPermGraphNodeAccess } from './permGraphNodeAccess'

type Props = {
  currentUserId: PermissionEntityId | undefined
  domId: string
  editor?: ReactNode
  isOpen: boolean
  node: PermGraphNode
  width: number
  onHoverChange: (isHovered: boolean) => void
  onToggle: () => void
}

export function PermGraphNodeCard({
  currentUserId,
  domId,
  editor,
  isOpen,
  node,
  width,
  onHoverChange,
  onToggle,
}: Props): ReactNode {
  const access = getPermGraphNodeAccess({ currentUserId, node })
  const borderRole = getPermGraphBorderRole(node)
  const theme = getPermGraphNodeTheme(node)
  const showLabel = borderRole === 'applied'

  return (
    <div className='relative' style={{ width }}>
      {showLabel && <PermGraphWinnerLabel />}
      <div
        className='relative flex flex-col overflow-hidden rounded-xl'
        data-access-tone={getPermGraphAccessTone(node)}
        data-border-role={borderRole}
        data-node-id={domId}
        data-open={isOpen || undefined}
        style={{
          background: theme.background,
          borderColor: theme.border,
          borderStyle: 'solid',
          borderWidth: theme.borderWidth,
          color: theme.text,
          transition: 'background-color 180ms ease, border-color 180ms ease',
        }}
      >
        <UnstyledButton
          aria-controls={isOpen ? `${domId}-editor` : undefined}
          aria-expanded={isOpen}
          aria-label={`${isOpen ? 'Close' : 'Open'} ${node.label}, ${getPermGraphNodeStateLabel(node)}`}
          className='gap-2xl px-3xl py-2xl flex items-center focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-blue-500'
          onBlur={() => onHoverChange(false)}
          onClick={onToggle}
          onFocus={() => onHoverChange(true)}
          onMouseEnter={() => onHoverChange(true)}
          onMouseLeave={() => onHoverChange(false)}
        >
          <MaterialSymbolIcon color={theme.text} icon={getPermGraphNodeIcon(node.kind)} size={22} />
          <Text className='min-w-0 flex-1 font-semibold text-pretty' lineClamp={2} size='md' ta='left'>
            {node.label}
          </Text>
          <div className='gap-xl flex max-w-[132px] shrink-0 items-center'>
            <Text
              className='px-xl py-sm rounded-full font-semibold'
              lineClamp={1}
              size='xs'
              style={{ background: theme.pillBackground }}
              ta='right'
            >
              {stringifyPermissionScope(node.flowScope ?? 'none')}
            </Text>
            <MaterialSymbolIcon
              className={access.canEdit ? 'opacity-0' : 'opacity-50'}
              color={theme.text}
              icon='lock'
              size={18}
            />
          </div>
        </UnstyledButton>
        {isOpen && editor && (
          <div className='bg-body px-3xl py-2xl border-t border-t-black/10' id={`${domId}-editor`}>
            {editor}
          </div>
        )}
      </div>
    </div>
  )
}
